import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import productData from "../Datas/productData";
import "../css/productdetails.scss";
import { CartContext } from "../Component/CartContext";

const formatPrice = (price) =>
  `$${Number(price).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const getOptionLabels = () => ({ color: "颜色", size: "规格" });

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = productData.find((item) => item.id === parseInt(id));
  const { addToCart } = useContext(CartContext);

  const [selectedSize, setSelectedSize] = useState(product?.size?.[0] || "");
  const [selectedColor, setSelectedColor] = useState(
    product?.colors?.[0] || ""
  );
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [openAccordions, setOpenAccordions] = useState([]);
  const [addedToCart, setAddedToCart] = useState(false);

  useEffect(() => {
    if (!product) return;
    setSelectedSize(product.size[0]);
    setSelectedColor(product.colors[0]);
    setSelectedImage(0);
    setQuantity(1);
    setAddedToCart(false);
    setOpenAccordions([]);
  }, [product]);

  const relatedProducts = useMemo(() => {
    if (!product) return [];

    const sameCategory = productData.filter(
      (item) => item.id !== product.id && item.category === product.category
    );
    const remaining = productData.filter(
      (item) => item.id !== product.id && item.category !== product.category
    );

    return [...sameCategory, ...remaining].slice(0, 4);
  }, [product]);

  const toTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  if (!product) {
    return (
      <main
        className="product-detail product-detail--not-found"
        id="MainContent"
      >
        <div className="product-detail__not-found">
          <h2>Product not found</h2>
          <p>
            The item may have moved or is no longer part of this collection.
          </p>
          <Link onClick={toTop} to="/shop">
            Back to Shop
          </Link>
        </div>
      </main>
    );
  }

  const handleAddToCart = () => {
    addToCart(product.id, quantity, selectedSize, selectedColor);
    setAddedToCart(true);
  };

  const handleBuyNow = () => {
    addToCart(product.id, quantity, selectedSize, selectedColor);
    navigate("/checkout");
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const moveImage = (direction) => {
    setSelectedImage((current) => {
      const next = current + direction;
      if (next < 0) return product.images.length - 1;
      if (next >= product.images.length) return 0;
      return next;
    });
  };

  const optionLabels = getOptionLabels(product.category);

  const accordionSections = [
    {
      id: "details",
      title: "商品详情",
      content: (
        <div className="product-detail__accordion-copy">
          {product.description.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
          <ul>
            {product.Features.map((feature) => (
              <li key={feature}>{feature}</li>
            ))}
          </ul>
        </div>
      ),
    },
    {
      id: "materials",
      title: "材质与使用提示",
      content: (
        <div className="product-detail__accordion-copy">
          <p>{product.material}.</p>
          <p>
            首次使用前请清洗并充分晾干；是否可微波、冷冻或洗碗机清洗，请以实际商品说明为准。
          </p>
        </div>
      ),
    },
    {
      id: "shipping",
      title: "配送信息",
      content: (
        <div className="product-detail__accordion-copy">
          <p>
            可配送地区与最终运费以结算页显示为准。
          </p>
          <Link to="/shipping" onClick={toTop}>
            查看配送政策
          </Link>
        </div>
      ),
    },
  ];

  return (
    <main className="product-detail" id="MainContent">
      <div className="product-detail__product">
        <div className="product-detail__layout">
          <section
            className="product-detail__gallery"
            aria-label="Product images"
          >
            <div className="product-detail__main-image">
              <img src={product.images[selectedImage]} alt={product.name} />
              {product.images.length > 1 && (
                <>
                  <button
                    type="button"
                    className="product-detail__gallery-arrow product-detail__gallery-arrow--previous"
                    onClick={() => moveImage(-1)}
                    aria-label="Show previous image"
                  >
                    <span aria-hidden="true">‹</span>
                  </button>
                  <button
                    type="button"
                    className="product-detail__gallery-arrow product-detail__gallery-arrow--next"
                    onClick={() => moveImage(1)}
                    aria-label="Show next image"
                  >
                    <span aria-hidden="true">›</span>
                  </button>
                </>
              )}
              <span className="product-detail__image-count">
                {selectedImage + 1} / {product.images.length}
              </span>
            </div>

            <div className="product-detail__thumbnails">
              {product.images.map((image, index) => (
                <button
                  type="button"
                  key={`${product.id}-${index}`}
                  className={index === selectedImage ? "is-active" : ""}
                  onClick={() => setSelectedImage(index)}
                  aria-label={`Show image ${index + 1}`}
                  aria-pressed={index === selectedImage}
                >
                  <img src={image} alt="" />
                </button>
              ))}
            </div>
          </section>

          <section className="product-detail__info">
            <div className="product-detail__heading">
              <h1>{product.name}</h1>
              <p className="product-detail__price">
                {formatPrice(product.price)}
              </p>
            </div>

            <p className="product-detail__lead">{product.description[0]}</p>

            <div className="product-detail__options">
              <p className="product-detail__options-title">可选规格：</p>

              <fieldset>
                <legend>{optionLabels.color}</legend>
                <div className="product-detail__option-list">
                  {product.colors.map((color) => (
                    <label key={color}>
                      <input
                        type="radio"
                        name={`color-${product.id}`}
                        value={color}
                        checked={selectedColor === color}
                        onChange={() => {
                          setSelectedColor(color);
                          setAddedToCart(false);
                        }}
                      />
                      <span>{color}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <fieldset>
                <legend>{optionLabels.size}</legend>
                <div className="product-detail__option-list">
                  {product.size.map((size) => (
                    <label key={size}>
                      <input
                        type="radio"
                        name={`size-${product.id}`}
                        value={size}
                        checked={selectedSize === size}
                        onChange={() => {
                          setSelectedSize(size);
                          setAddedToCart(false);
                        }}
                      />
                      <span>{size}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            </div>

            <div className="product-detail__quantity-block">
              <p>Quantity:</p>
              <div className="product-detail__quantity">
                <button
                  type="button"
                  onClick={() =>
                    setQuantity((current) => Math.max(1, current - 1))
                  }
                  disabled={quantity === 1}
                  aria-label="Decrease quantity"
                >
                  −
                </button>
                <span aria-live="polite">{quantity}</span>
                <button
                  type="button"
                  onClick={() => setQuantity((current) => current + 1)}
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>
            </div>

            <div className="product-detail__actions">
              <button
                type="button"
                className="product-detail__add"
                onClick={handleAddToCart}
              >
                {addedToCart ? "Added to cart" : "Add to cart"}
              </button>
              <button
                type="button"
                className="product-detail__buy"
                onClick={handleBuyNow}
              >
                Buy it now
              </button>
            </div>

            {addedToCart && (
              <p className="product-detail__added" role="status">
                This item is in your cart. <Link to="/cartpage">View cart</Link>
              </p>
            )}

            <p className="product-detail__checkout-note">
              Shipping, taxes, and discounts are calculated at checkout.
            </p>
          </section>
        </div>
      </div>

      <section
        className="product-detail__accordions"
        aria-label="Product information"
      >
        {accordionSections.map((section) => {
          const isOpen = openAccordions.includes(section.id);
          return (
            <div
              className={`product-detail__accordion ${isOpen ? "is-open" : ""}`}
              key={section.id}
            >
              <button
                type="button"
                onClick={() =>
                  setOpenAccordions((current) =>
                    isOpen
                      ? current.filter((item) => item !== section.id)
                      : [...current, section.id]
                  )
                }
                aria-expanded={isOpen}
                aria-controls={`product-panel-${section.id}`}
              >
                <span>{section.title}</span>
                <span
                  className="product-detail__accordion-icon"
                  aria-hidden="true"
                />
              </button>
              <div
                id={`product-panel-${section.id}`}
                className="product-detail__accordion-panel"
                hidden={!isOpen}
              >
                {section.content}
              </div>
            </div>
          );
        })}
      </section>

      <section
        className="product-detail__related"
        aria-labelledby="related-title"
      >
        <h2 id="related-title">You may also like</h2>
        <div className="product-detail__related-grid">
          {relatedProducts.map((item) => (
            <Link
              to={`/shopDetails/${item.id}`}
              onClick={toTop}
              className="product-detail__related-card"
              key={item.id}
            >
              <span className="product-detail__related-image">
                <img src={item.image} alt={item.name} />
              </span>
              <span className="product-detail__related-name">{item.name}</span>
              <span className="product-detail__related-price">
                {formatPrice(item.price)}
              </span>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
};

export default ProductDetail;
